import { Button, Progress } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import useBudgetTracking from 'src/Hooks/Queries/useBudgetTracking';
import useBudgetYear from 'src/Hooks/Queries/useBudgetYear';
import useFetchExpenseStats from 'src/Hooks/Queries/useFetchExpenseStats';
import { useHasPermission } from 'src/Hooks/Shared/useHasPermission';

import styles from './BudgetYearCard.module.scss';
import { Props } from './BudgetYearCard.types';
import { DashboardCard } from './DashboardCard';

const formatEur = (amount: number): string =>
  `${amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

const BudgetYearCard = ({ year, onPrepareBudget }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useFetchExpenseStats(year.toString());
  const { data: budgetYear, isLoading: isBudgetYearLoading } = useBudgetYear(year);
  const { data: tracking } = useBudgetTracking(year);
  const { hasPermission: canManageFinance } = useHasPermission('financeAdmin', 'full');

  // One cap, and it is the one Prévisionnel computes from the budget lines. The hub used to
  // show a separate figure typed by hand into a modal, so the same house had a 750 € cap here
  // and a 635 € cap two clicks away.
  const rawCap = budgetYear?.calculatedCap ?? null;
  // A cap of 0 is treated as no cap at all, so we never divide by zero.
  const cap = rawCap != null && rawCap > 0 ? rawCap : null;
  // One realised amount too: everything spent this year, budgeted or not — the same figure
  // the Suivre gauge shows. Falls back on the stats total while tracking loads.
  const spent = tracking?.spentTotal ?? data?.yearlyExpenseTotal ?? 0;
  const expenses = data?.totalExpenses ?? 0;
  const incomes = data?.totalRevenues ?? 0;

  const percent = cap ? Math.round((spent / cap) * 100) : null;
  const isOverCap = percent != null && percent > 100;

  const subline =
    cap == null
      ? t('expenses.dashboard.spentNoBudget', 'dépensés, aucun budget préparé')
      : isOverCap
        ? t(
            'expenses.dashboard.spentOverCap',
            'dépensés, dépassement de {{over}} sur {{cap}} prévus',
            { over: formatEur(spent - cap), cap: formatEur(cap) }
          )
        : t('expenses.dashboard.spentOfCap', 'dépensés, soit {{pct}} % des {{cap}} prévus', {
            pct: percent,
            cap: formatEur(cap),
          });

  return (
    <DashboardCard
      label={t('expenses.dashboard.budgetYear', "Budget de l'année {{year}}", { year })}
      figure={formatEur(spent)}
      subline={<span>{subline}</span>}
      isLoading={isLoading || isBudgetYearLoading}
      isError={isError}
      onRetry={() => {
        void refetch();
      }}
      extra={
        <>
          {percent != null && (
            <Progress
              size="sm"
              mt={8}
              value={Math.min(percent, 100)}
              color={isOverCap ? 'red' : 'green'}
              aria-label={t('expenses.budgetCap.label', 'Budget plafond annuel')}
            />
          )}
          <div className={styles.miniStats}>
            <div className={styles.miniStat}>
              <span className={styles.miniStatLabel}>
                {t('expenses.stats.expenseLabel', 'Dépenses')}
              </span>
              <span className={styles.miniStatExpense}>{formatEur(expenses)}</span>
            </div>
            <div className={styles.miniStat}>
              <span className={styles.miniStatLabel}>
                {t('expenses.stats.incomeLabel', 'Recettes')}
              </span>
              <span className={styles.miniStatIncome}>{formatEur(incomes)}</span>
            </div>
          </div>
        </>
      }
      actions={
        // Finance admins only: everybody else simply does not see the button.
        canManageFinance ? (
          <Button size="sm" variant="outline" onClick={onPrepareBudget}>
            {cap == null
              ? t('expenses.dashboard.prepareBudget', 'Préparer mon budget {{year}}', { year })
              : t('expenses.dashboard.adjustBudget', 'Ajuster le budget')}
          </Button>
        ) : undefined
      }
    />
  );
};

export default BudgetYearCard;
